<template>
  <div class="DropdownMenu" ref="menuRef">
    <div ref="activatorRef" @click.stop="toggleMenu">
      <slot name="activator"></slot>
    </div>

    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="isOpen"
          ref="menuContentRef"
          class="DropdownMenu__content"
          :class="`DropdownMenu__content--${location}`"
          :style="menuStyle"
          @click.stop
        >
          <slot></slot>
        </div>
      </Transition>

      <div v-if="isOpen" class="DropdownMenu__backdrop" @click="closeMenu"></div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

const props = withDefaults(
  defineProps<{
    location?: 'bottom' | 'top' | 'left' | 'right'
    activator?: 'parent' | 'click'
  }>(),
  {
    location: 'bottom',
    activator: 'click',
  },
)

const menuRef = ref<HTMLElement>()
const activatorRef = ref<HTMLElement>()
const menuContentRef = ref<HTMLElement>()
const isOpen = ref(false)
const menuStyle = ref<Record<string, string>>({})

const toggleMenu = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(() => {
      updateMenuPosition()
    })
  }
}

const closeMenu = () => {
  isOpen.value = false
}

const updateMenuPosition = () => {
  if (!activatorRef.value || !menuContentRef.value) return

  const activatorRect = activatorRef.value.getBoundingClientRect()
  const menuRect = menuContentRef.value.getBoundingClientRect()

  let top = 0
  let left = 0

  switch (props.location) {
    case 'bottom':
      top = activatorRect.bottom + 8
      left = activatorRect.left
      break
    case 'top':
      top = activatorRect.top - menuRect.height - 8
      left = activatorRect.left
      break
    case 'right':
      top = activatorRect.top
      left = activatorRect.right + 8
      break
    case 'left':
      top = activatorRect.top
      left = activatorRect.left - menuRect.width - 8
      break
  }

  // Ajuster si le menu dépasse de l'écran
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  if (left + menuRect.width > viewportWidth) {
    left = viewportWidth - menuRect.width - 8
  }
  if (left < 8) {
    left = 8
  }

  if (top + menuRect.height > viewportHeight) {
    top = viewportHeight - menuRect.height - 8
  }
  if (top < 8) {
    top = 8
  }

  menuStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
  }
}

const handleClickOutside = (event: MouseEvent) => {
  if (
    menuRef.value &&
    !menuRef.value.contains(event.target as Node) &&
    menuContentRef.value &&
    !menuContentRef.value.contains(event.target as Node)
  ) {
    closeMenu()
  }
}

watch(isOpen, (newValue) => {
  if (newValue) {
    document.addEventListener('click', handleClickOutside)
    window.addEventListener('resize', updateMenuPosition)
    window.addEventListener('scroll', updateMenuPosition, true)
  } else {
    document.removeEventListener('click', handleClickOutside)
    window.removeEventListener('resize', updateMenuPosition)
    window.removeEventListener('scroll', updateMenuPosition, true)
  }
})

onMounted(() => {
  if (props.activator === 'parent' && menuRef.value?.parentElement) {
    activatorRef.value = menuRef.value.parentElement as HTMLElement
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})

defineExpose({
  isOpen,
  toggleMenu,
  closeMenu,
})
</script>

<style scoped>
.DropdownMenu {
  position: relative;
  display: inline-block;
}

.DropdownMenu__content {
  position: fixed;
  background: white;
  border: 1px solid rgb(var(--v-theme-main-grey));
  border-radius: var(--dim-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
  max-width: 400px;
}

.DropdownMenu__backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: transparent;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
